import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { GiftCard } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromGiftCard, toGiftCardUpdate } from '../api/mappers';
import { useUpdateGiftCard } from '../hooks/use-gift-card-mutations';
import {
  giftCardEditSchema,
  type GiftCardEditInput,
  type GiftCardEditOutput,
} from '../schemas/gift-card-form-schema';
import { toExpiryInput } from '../schemas/gift-card-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['expiresAt', 'note']);

/**
 * Expiry and internal note; the balance only moves through the ledger. The expiry is sent only
 * when it changed, so the note of an expired card can still be saved.
 */
export function GiftCardEditCard({
  storeId,
  card,
  readOnly,
}: {
  storeId: string;
  card: GiftCard;
  readOnly: boolean;
}) {
  const { t } = useTranslation(['giftCards', 'common']);
  const update = useUpdateGiftCard(storeId, 'giftCards:toasts.updated');
  const message = useErrorMessage();
  const currentExpiry = toExpiryInput(card.expiresAt);
  const schema = useMemo(() => giftCardEditSchema(currentExpiry), [currentExpiry]);
  const form = useForm<GiftCardEditInput, unknown, GiftCardEditOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: fromGiftCard(card),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ id: card.id, body: toGiftCardUpdate(values, card) });
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return (
    <FormSection title={t('giftCards:details.title')}>
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="expiresAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('giftCards:fields.expiresAt')}</FormLabel>
                <FormControl>
                  <DatePicker disabled={readOnly} {...field} />
                </FormControl>
                <FormDescription>{t('giftCards:fields.expiresAtHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('giftCards:fields.note')}</FormLabel>
                <FormControl>
                  <Textarea rows={3} disabled={readOnly} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {readOnly ? null : (
            <div className="flex gap-2">
              <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}
